/**
 * AuthContext shim. Delegates state + bootstrap to @wg/hooks/auth's
 * AuthProvider; re-exports the same `AuthContext` symbol the rest of the
 * web app already imports (ChatSession etc consume `useContext(AuthContext)`).
 */
import React from 'react';
import {
  AuthProvider as SharedAuthProvider,
  useAuthContext,
  type AuthContextValue,
  type AuthState,
} from '@wg/hooks/auth';
import { auth } from '../client';

export type { AuthState, AuthContextValue };

// Bridge: existing call-sites use `useContext(AuthContext)` so we expose a
// thin context object that delegates to the shared one. Keeping the exact
// same name avoids touching ~10 consumers in this PR.
const AuthContextBridge = React.createContext<AuthContextValue>({
  authState: null,
  setAuthState: () => {},
  isAuthenticated: false,
  isGuest: false,
  sessionInfo: null,
});

const Bridge = ({ children }: { children: React.ReactNode }) => {
  const value = useAuthContext();
  return (
    <AuthContextBridge.Provider value={value}>
      {children}
    </AuthContextBridge.Provider>
  );
};

export const AuthContext = AuthContextBridge;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => (
  <SharedAuthProvider core={auth}>
    <Bridge>{children}</Bridge>
  </SharedAuthProvider>
);

export default AuthContext;
